import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, PlusCircle, Search, Filter, User, DollarSign, ChevronRight, AlertCircle } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface Property {
  id: number;
  address: string;
  type: string;
  bedrooms?: number;
  bathrooms?: number;
  rentAmount?: number;
  rentFrequency?: string;
  status: string;
  tenantName?: string;
  agentId?: number;
  agentName?: string;
}

export default function OwnerProperties() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/owner/properties');
      const propsList = Array.isArray(response) ? response : (response.content || []);
      setProperties(propsList);
    } catch (err) {
      console.error('Failed to load properties:', err);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(property => {
    const matchesSearch = !searchTerm ||
      property.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.tenantName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || property.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: properties.length,
    occupied: properties.filter(p => p.status === 'OCCUPIED').length,
    vacant: properties.filter(p => p.status === 'VACANT').length,
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OCCUPIED': return 'bg-green-100 text-green-800';
      case 'VACANT': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRent = (amount?: number, frequency?: string) => {
    if (!amount) return 'Not set';
    const freq = frequency?.toLowerCase().replace('ly', '') || 'week';
    return `$${amount.toLocaleString()}/${freq}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="My Properties"
        subtitle="Manage your investment properties"
        icon={<Building className="w-8 h-8" />}
        backPath="/owner/dashboard"
      >
        <button
          onClick={() => navigate('/owner/properties/onboard')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm"
        >
          <PlusCircle size={16} />
          Add Property
        </button>
      </PageHeader>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <button
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Properties</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Building className="w-8 h-8 text-gray-200" />
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('OCCUPIED')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${statusFilter === 'OCCUPIED' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Occupied</p>
              <p className="text-2xl font-bold text-green-600">{stats.occupied}</p>
            </div>
            <User className="w-8 h-8 text-green-100" />
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('VACANT')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${statusFilter === 'VACANT' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Vacant</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.vacant}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-100" />
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by address or tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            >
              <option value="all">All Properties</option>
              <option value="OCCUPIED">Occupied</option>
              <option value="VACANT">Vacant</option>
            </select>
          </div>
        </div>
      </div>

      {/* Properties List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {statusFilter === 'all' ? 'All Properties' : `${statusFilter} Properties`}
          </h2>
        </div>

        {filteredProperties.length === 0 ? (
          <div className="p-12 text-center">
            <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No properties found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Try adjusting your search terms.' : 'Add your first property to get started.'}
            </p>
            <button
              onClick={() => navigate('/owner/properties/onboard')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-10 text-[13px] font-semibold text-white bg-forest-900 hover:bg-forest-800 transition-colors shadow-sm"
            >
              <PlusCircle size={16} />
              Add Property
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredProperties.map(property => (
              <div
                key={property.id}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/owner/properties/${property.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.status)}`}>
                        {property.status}
                      </span>
                      <span className="text-sm text-gray-500">
                        {property.type || 'Property'}
                        {property.bedrooms && ` • ${property.bedrooms} bed`}
                        {property.bathrooms && ` • ${property.bathrooms} bath`}
                      </span>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-2">{property.address}</h3>

                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      {property.tenantName && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {property.tenantName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {formatRent(property.rentAmount, property.rentFrequency)}
                      </span>
                      {property.agentName && (
                        <span className="text-secondary">
                          Agent: {property.agentName}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


